// COLORS
export const COLORS = {
  primary: "#534AB7",
  primaryDark: "#3C3489",
  secondary: "#64748b",
  success: "#16a34a",
  successLight: "#dcfce7",
  danger: "#ef4444",
  dangerLight: "#fee2e2",
  warning: "#f59e0b",
  white: "#ffffff",
  textPrimary: "#1e293b",
  textSecondary: "#374151",
  textMuted: "#64748b",
  border: "#d1d5db",
  borderLight: "#f1f5f9",
  background: "#f8fafc",
  tableHeader: "#b1b9c1",
};

export const COMMON = {
  borderRadius: 8,
  cardRadius: 10,
  transition: "0.2s ease",
  shadow: "0 1px 4px rgba(0,0,0,0.06)",
  activeShadow: "0 2px 8px rgba(83,74,183,0.18)",
};

export const ROLE_COLORS = {
  admin: "#dbeafe",
  "senior-manager": "#e0e7ff",
  manager: "#d1fae5",
  sales: "#fef9c3",
};

export const ROLE_TEXT_COLORS = {
  admin: "#1d4ed8",
  "senior-manager": "#4338ca",
  manager: "#065f46",
  sales: "#92400e",
};

export const STATUS_COLORS = {
  active: "#fef3c7",
  completed: "#dcfce7",
  missing: "#fee2e2",
};

const BADGE_BASE = {
  padding: "4px 8px",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  display: "inline-block",
  textTransform: "capitalize",
};

const STAGE_BADGE_STYLES = {
  prospecting: { background: "#ede9fe", color: "#8b5cf6" },
  contacted: { background: "#e0f2fe", color: "#0284c7" },
  demo_scheduled: { background: "#dbeafe", color: "#3b82f6" },
  demo_done: { background: "#dcfce7", color: "#059669" },
  negotiation: { background: "#fef3c7", color: "#d97706" },
  closed_won: { background: "#dcfce7", color: "#10b981" },
  closed_lost: { background: "#fee2e2", color: "#ef4444" },
  lead_closed: { background: "#dcfce7", color: "#10b981" },
};

const STATUS_BADGE_STYLES = {
  active: { background: "#fef3c7", color: "#f59e0b" },
  pending: { background: "#fef3c7", color: "#d97706" },
  in_progress: { background: "#dbeafe", color: "#3b82f6" },
  no_response: { background: "#fee2e2", color: "#ef4444" },
  confirmed: { background: "#dbeafe", color: "#2563eb" },
  scheduled: { background: "#dbeafe", color: "#2563eb" },
  not_scheduled: { background: "#f1f5f9", color: "#64748b" },
  cancelled: { background: "#fee2e2", color: "#ef4444" },
  completed: { background: "#dcfce7", color: "#059669" },
  on_hold: { background: "#fef3c7", color: "#d97706" },
  closed_won: { background: "#dcfce7", color: "#10b981" },
  closed_lost: { background: "#fee2e2", color: "#ef4444" },
  won: { background: "#dcfce7", color: "#10b981" },
  lost: { background: "#fee2e2", color: "#ef4444" },
  negotiated: { background: "#fef3c7", color: "#d97706" },
  not_interested: { background: "#fee2e2", color: "#dc2626" },
  missing: { background: "#fee2e2", color: "#dc2626" },
};

const globalStyles = {
  contentWrapper: {
    marginTop: -15,
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },

  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 20,
  },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textSecondary,
    marginBottom: 14,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    gap: 12,
    flexWrap: "wrap",
  },

  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    marginTop: 8,
    flexWrap: "nowrap",
    width: "100%",
  },

  filterLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
    width: "100%",
  },

  tilesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    width: "100%",
    marginBottom: 18,
  },

  tile: {
    background: COLORS.white,
    padding: "18px 20px",
    borderRadius: 10,
    boxShadow: COMMON.shadow,
    border: `1px solid ${COLORS.borderLight}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 88,
    cursor: "pointer",
  },

  activeTile: {
    border: `1px solid ${COLORS.primary}`,
    boxShadow: COMMON.activeShadow,
  },

  tileIcon: (color) => ({
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: `${color}18`,
    color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  tileText: {
    textAlign: "right",
  },

  tileValue: {
    fontSize: 24,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1,
  },

  tileLabel: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 6,
    fontWeight: 500,
  },

  h1: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  h2: { fontSize: 18, fontWeight: 600, marginBottom: 20 },

  btn: {
    padding: "9px 18px",
    background: COLORS.primary,
    color: COLORS.white,
    border: "none",
    borderRadius: COMMON.borderRadius,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 7,
    transition: COMMON.transition,
  },

  btnRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },

  deleteBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.danger,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  editBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.primary,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  viewBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.textSecondary,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mediaBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.warning,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  backBtn: {
    fontSize: 13,
    color: COLORS.primary,
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontWeight: 500,
  },

  submitBtn: {
    width: "100%",
    height: 44,
    background: COLORS.primary,
    color: COLORS.white,
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: COMMON.transition,
  },

  submitBtnHover: {
    background: COLORS.primaryDark,
    transform: "scale(0.99)",
  },

  submitBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 18,
    color: "#6b7280",
  },

  mapBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.textSecondary,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  mapOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 12,
  },

  mapCard: {
    width: "min(500px, 94vw)",
    background: COLORS.white,
    borderRadius: 12,
    overflow: "hidden",
    padding: 18,
    boxSizing: "border-box",
  },

  mapHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },

  mapTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },

  mapCoords: {
    fontSize: 13,
    color: COLORS.textMuted,
    marginTop: 4,
  },

  mapFrame: {
    border: "none",
    borderRadius: 10,
    width: "100%",
  },

  mapActionBtn: {
    marginTop: 14,
    width: "100%",
    border: "none",
    background: COLORS.primary,
    color: COLORS.white,
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 500,
  },

  info: {
    fontSize: 12,
    color: COLORS.textMuted,
    background: COLORS.borderLight,
    padding: "6px 10px",
    borderRadius: 6,
    marginBottom: 14,
  },

  statusBadge: (status) => ({
    ...BADGE_BASE,
    ...(STATUS_BADGE_STYLES[status] || {
      background: COLORS.background,
      color: COLORS.textMuted,
    }),
  }),

  stageBadge: (stage) => ({
    ...BADGE_BASE,
    ...(STAGE_BADGE_STYLES[stage] || {
      background: COLORS.background,
      color: COLORS.textMuted,
    }),
  }),

  badge(status) {
    return this.statusBadge(status);
  },

  card: {
    background: COLORS.white,
    borderRadius: COMMON.cardRadius,
    boxShadow: COMMON.shadow,
    border: `1px solid ${COLORS.borderLight}`,
  },

  searchWrap: {
    position: "relative",
    width: "100%",
    maxWidth: 299,
  },

  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: COLORS.primary,
    fontSize: 16,
  },

  searchInput: {
    width: "100%",
    padding: "10px 12px 10px 36px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 13,
    outline: "none",
    background: COLORS.background,
    boxSizing: "border-box",
  },

  filterSelect: {
    width: 180,
    maxWidth: 190,
    padding: "10px 12px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 13,
    outline: "none",
    background: COLORS.background,
    boxSizing: "border-box",
    color: "#374151",
    cursor: "pointer",
  },

  tableScroll: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  table: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    background: COLORS.white,
    borderRadius: COMMON.cardRadius,
    overflow: "hidden",
    boxShadow: COMMON.shadow,
  },

  th: {
    background: COLORS.tableHeader,
    padding: "8px 16px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.textSecondary,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
  },

  td: {
    padding: "7px 14px",
    fontSize: 12,
    color: COLORS.textSecondary,
    borderBottom: `1px solid ${COLORS.borderLight}`,
    whiteSpace: "nowrap",
  },

  label: {
    display: "block",
    marginBottom: 5,
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.textSecondary,
  },

  input: {
    width: "100%",
    padding: "9px 12px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 12,
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    padding: "9px 12px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 12,
    minHeight: 90,
    resize: "vertical",
    boxSizing: "border-box",
  },

  select: {
    width: "100%",
    padding: "9px 12px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 14,
    boxSizing: "border-box",
    background: COLORS.white,
  },

  inputError: {
    borderColor: COLORS.danger,
  },

  errorText: {
    fontSize: 12,
    color: COLORS.danger,
    marginTop: -8,
    marginBottom: 10,
  },

  errorBanner: {
    color: "#991b1b",
    fontSize: 13,
    marginBottom: 14,
    background: COLORS.dangerLight,
    padding: "8px 12px",
    borderRadius: 6,
  },

  successBanner: {
    color: "#166534",
    fontSize: 13,
    marginBottom: 14,
    background: COLORS.successLight,
    padding: "8px 12px",
    borderRadius: 6,
  },

  paginateRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    flexWrap: "wrap",
  },

  paginateBtn: {
    border: `0.5px solid ${COLORS.primary}`,
    padding: "4px 10px",
    background: COLORS.background,
    color: COLORS.primary,
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },

  paginatePageInfo: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontWeight: 500,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  emptyState: {
    textAlign: "center",
    padding: 34,
    fontSize: 13,
    color: COLORS.textMuted,
  },

  loading: {
    fontSize: 14,
    color: COLORS.textSecondary,
  },

  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: 12,
  },

  mcard: {
    background: COLORS.white,
    padding: 32,
    borderRadius: 12,
    width: "min(720px, 94vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    boxSizing: "border-box",
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },

  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },

  modalCloseBtn: {
    border: "none",
    background: COLORS.background,
    color: COLORS.textMuted,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  viewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },

  viewItem: {
    background: COLORS.background,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${COLORS.borderLight}`,
  },

  viewItemFull: {
    gridColumn: "1 / -1",
    background: COLORS.background,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${COLORS.borderLight}`,
  },

  viewLabel: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 4,
  },

  viewValue: {
    fontSize: 14,
    color: COLORS.textPrimary,
    fontWeight: 500,
    wordBreak: "break-word",
  },

  mrow: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 14,
    flexWrap: "wrap",
  },

  srow: { display: "flex", gap: 10 },
  half: { flex: 1 },
  full: { width: "100%" },

  cancel: {
    padding: "9px 16px",
    border: `1px solid ${COLORS.border}`,
    background: COLORS.white,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },

  toggleWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  toggleBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  roleBadge: (role) => ({
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    display: "inline-block",
    textTransform: "capitalize",
    background: ROLE_COLORS[role] || COLORS.background,
    color: ROLE_TEXT_COLORS[role] || COLORS.textSecondary,
    textTransform: "capitalize",
    letterSpacing: "0.2px",
  }),
  responsive: {
    mobileToolbar: {
      flexDirection: "column",
      alignItems: "stretch",
    },

    mobileFilterLeft: {
      flexDirection: "column",
      alignItems: "stretch",
      width: "100%",
    },

    mobileSearchWrap: {
      maxWidth: "100%",
    },

    mobileFilterSelect: {
      width: "100%",
      maxWidth: "100%",
    },

    mobileTilesGrid: {
      gridTemplateColumns: "1fr",
    },

    mobileModalCard: {
      width: "94vw",
      padding: 18,
    },

    mobileFormRow: {
      flexDirection: "column",
    },

    mobileContentWrapper: {
      marginTop: 0,
    },

    mobilePageTitle: {
      fontSize: 20,
    },
  },
};

export default globalStyles;
